import { getErrorMessage, useToast } from '@/app/providers';
import { Button } from '@/core/ui/components/Button';
import { Input } from '@/core/ui/components/Input';
import { useAbility } from '@/modules/identity/application/ability';
import { SAT_REGIMES } from '@/shared/constants';
import { useI18n } from '@/shared/i18n';
import type { FiscalData, User } from '@/shared/types';
import { Subjects } from '@yawi/casl';
import { Edit2, FileText, Lock, Mail, Phone, Save } from 'lucide-react';
import React, { useState } from 'react';

interface ProfileInfoTabProps {
  user: User;
  onUpdateFiscal: (fiscalData: FiscalData) => Promise<void>;
  onUpdateContact: (data: { name: string; phone: string }) => Promise<void>;
  onChangePassword: () => void;
}

// El backend acepta el teléfono como dígitos simples (E.164 sin espacios ni
// guiones ya vale), pero lo mostramos formateado igual que el resto de la app.
const scrubToDigits = (value: string) => value.replace(/\D/g, '').slice(0, 10);
const formatPhoneForDisplay = (digits: string) => {
  if (!digits) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

export const ProfileInfoTab: React.FC<ProfileInfoTabProps> = ({ user, onUpdateFiscal, onUpdateContact, onChangePassword }) => {
  const { t } = useI18n();
  const toast = useToast();
  const ability = useAbility();
  const canEditFiscal = ability.can('update', Subjects.FiscalData);
  const [isEditingFiscal, setIsEditingFiscal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fiscalForm, setFiscalForm] = useState<Partial<FiscalData>>({});

  const [isEditingContact, setIsEditingContact] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', phone: '' });

  const startEditingContact = () => {
    setContactForm({ name: user.name, phone: user.phone || '' });
    setIsEditingContact(true);
  };

  const saveContact = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactLoading(true);
    try {
      await onUpdateContact({ name: contactForm.name.trim(), phone: contactForm.phone });
      setIsEditingContact(false);
      toast.success('Información de contacto actualizada correctamente.');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setContactLoading(false);
    }
  };

  const startEditingFiscal = () => {
    setFiscalForm(user.fiscalData || {
      rfc: '',
      businessName: '',
      taxRegime: '',
      zipCode: '',
      address: '',
      email: user.email
    });
    setIsEditingFiscal(true);
  };

  const saveFiscalData = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onUpdateFiscal(fiscalForm as FiscalData);
      setIsEditingFiscal(false);
      toast.success('Datos fiscales actualizados correctamente.');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-in fade-in">
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-bold text-theme-secondary uppercase tracking-wider">Información de Contacto</h3>
          {!isEditingContact && (
            <button onClick={startEditingContact} className="text-primary-600 dark:text-primary-400 hover:text-primary-800 text-xs font-bold flex items-center gap-1">
              <Edit2 size={14} /> Editar
            </button>
          )}
        </div>

        {isEditingContact ? (
          <form onSubmit={saveContact} className="bg-surface-elevated p-6 rounded-xl border border-theme space-y-4 animate-in fade-in">
            <Input
              label="Nombre completo"
              value={contactForm.name}
              onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
              required
              minLength={2}
            />
            <Input
              label="Teléfono"
              type="tel"
              value={formatPhoneForDisplay(contactForm.phone)}
              onChange={(e) => setContactForm({ ...contactForm, phone: scrubToDigits(e.target.value) })}
              placeholder="(000) 000-0000"
            />
            <div>
              <p className="text-xs text-theme-secondary font-bold mb-1.5">Email</p>
              <p className="text-theme-secondary text-sm px-1">{user.email} — no se puede cambiar aquí</p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="secondary" size="sm" className="flex-1" onClick={() => setIsEditingContact(false)}>Cancelar</Button>
              <Button type="submit" size="sm" className="flex-1" isLoading={contactLoading} icon={<Save size={14} />}>Guardar</Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-surface-elevated rounded-xl border border-theme">
              <div className="p-2 bg-surface rounded-lg shadow-sm text-blue-600 dark:text-blue-400"><Mail size={20} /></div>
              <div>
                <p className="text-xs text-theme-secondary font-bold">Email</p>
                <p className="text-theme font-medium">{user.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 p-4 bg-surface-elevated rounded-xl border border-theme">
              <div className="p-2 bg-surface rounded-lg shadow-sm text-emerald-600 dark:text-emerald-400"><Phone size={20} /></div>
              <div>
                <p className="text-xs text-theme-secondary font-bold">Teléfono</p>
                <p className="text-theme font-medium">{user.phone || 'No registrado'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Sección de Seguridad */}
        <div className="mt-8">
          <h3 className="text-sm font-bold text-theme-secondary uppercase tracking-wider mb-4">{t('settings.security')}</h3>
          <button
            onClick={onChangePassword}
            className="flex items-center gap-4 p-4 bg-surface-elevated rounded-xl border border-theme hover:bg-surface-elevated transition-colors w-full text-left"
          >
            <div className="p-2 bg-white dark:bg-zinc-700 rounded-lg shadow-sm text-amber-600 dark:text-amber-400"><Lock size={20} /></div>
            <div>
              <p className="text-theme font-medium">{t('profile.changePassword')}</p>
              <p className="text-xs text-theme-secondary">••••••••</p>
            </div>
          </button>
        </div>
      </div>

      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-bold text-theme-secondary uppercase tracking-wider">Datos Fiscales</h3>
          {!isEditingFiscal && canEditFiscal && (
            <button onClick={startEditingFiscal} className="text-primary-600 dark:text-primary-400 hover:text-primary-800 text-xs font-bold flex items-center gap-1">
              <Edit2 size={14} /> Editar
            </button>
          )}
        </div>

        {isEditingFiscal && canEditFiscal ? (
          <form onSubmit={saveFiscalData} className="bg-surface-elevated p-6 rounded-xl border border-theme space-y-4 animate-in fade-in">
            <Input label="Razón Social" value={fiscalForm.businessName || ''} onChange={e => setFiscalForm({ ...fiscalForm, businessName: e.target.value })} required />
            <Input label="RFC" value={fiscalForm.rfc || ''} onChange={e => setFiscalForm({ ...fiscalForm, rfc: e.target.value.toUpperCase() })} required />
            <div>
              <label className="block text-sm font-medium text-theme mb-1.5">Régimen Fiscal</label>
              <select className="w-full rounded-lg border border-theme bg-surface px-3 py-2.5 text-sm text-theme" value={fiscalForm.taxRegime || ''} onChange={e => setFiscalForm({ ...fiscalForm, taxRegime: e.target.value })}>
                <option value="">Seleccione...</option>
                {SAT_REGIMES.map(r => (<option key={r.code} value={`${r.code} - ${r.description}`}>{r.code} - {r.description}</option>))}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-1"><Input label="CP" value={fiscalForm.zipCode || ''} onChange={e => setFiscalForm({ ...fiscalForm, zipCode: e.target.value })} required /></div>
              <div className="col-span-2"><Input label="Email Facturación" value={fiscalForm.email || ''} onChange={e => setFiscalForm({ ...fiscalForm, email: e.target.value })} required /></div>
            </div>
            <Input label="Dirección Fiscal" value={fiscalForm.address || ''} onChange={e => setFiscalForm({ ...fiscalForm, address: e.target.value })} required />
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="secondary" size="sm" className="flex-1" onClick={() => setIsEditingFiscal(false)}>Cancelar</Button>
              <Button type="submit" size="sm" className="flex-1" isLoading={loading} icon={<Save size={14} />}>Guardar</Button>
            </div>
          </form>
        ) : (
          user.fiscalData ? (
            <div className="bg-slate-50 bg-surface-elevated p-6 rounded-xl border border-slate-100 dark:border-slate-600 space-y-3">
              <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400 text-sm">Razón Social</span><span className="font-bold text-slate-800 dark:text-slate-200 text-sm">{user.fiscalData.businessName}</span></div>
              <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400 text-sm">RFC</span><span className="font-mono text-slate-800 dark:text-slate-200 text-sm">{user.fiscalData.rfc}</span></div>
              <div className="pt-3 border-t border-slate-200 dark:border-slate-600"><p className="text-xs text-slate-400 dark:text-slate-500">{user.fiscalData.address}</p></div>
            </div>
          ) : (
            <div className="text-center p-8 bg-slate-50 bg-surface-elevated rounded-xl border border-dashed border-slate-200 dark:border-slate-600 text-slate-400 dark:text-slate-500 flex flex-col items-center">
              <FileText size={24} className="mb-2 opacity-50" />
              {canEditFiscal ? (
                <Button size="sm" variant="secondary" onClick={startEditingFiscal}>Registrar Ahora</Button>
              ) : (
                <p className="text-xs text-slate-400 dark:text-slate-500">No hay datos fiscales registrados</p>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};
